/**
 * @fileoverview The notification bell's data for the dashboard shell.
 * @description Resolves the in-app notification history, the unread count and the two mark-read
 * mutations, and maps them to the one `notifications` prop DashboardLayout takes (the bell dropdown
 * is purely presentational, and shared may not import a feature). The API's InAppNotification is
 * mapped to the dropdown's own item shape: the delivery id becomes `id`, and anything not UNREAD
 * counts as read. When the count endpoint has not answered, the unread items are counted instead.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotificationHistory,
  useUnreadNotificationCount,
} from '@/features/notifications'

import type { InAppNotification } from '@/features/notifications'
import type { DashboardHeaderNotificationsData } from '@/shared/components/molecules/DashboardHeader'
import type { DashboardHeaderNotificationItem } from '@/shared/components/molecules/DashboardHeaderNotifications'

/**
 * @description Maps the API's in-app notifications to the bell dropdown's plain item shape.
 * @param {InAppNotification[]} notifications - The notifications from GET /notifications.
 * @returns {DashboardHeaderNotificationItem[]} The dropdown items.
 */
function toNotificationItems(
  notifications: InAppNotification[],
): DashboardHeaderNotificationItem[] {
  return notifications.map((notification) => ({
    id: notification.notificationDeliveryId,
    title: notification.title ?? '',
    body: notification.body,
    sentAt: notification.createdAt,
    read: notification.inAppStatus !== 'UNREAD',
  }))
}

/**
 * @description Builds the notification bell's data and callbacks.
 * @returns {DashboardHeaderNotificationsData} The items, the unread count and the mark-read
 * callbacks.
 */
export function useDashboardNotifications(): DashboardHeaderNotificationsData {
  const historyQuery = useNotificationHistory()
  const unreadCountQuery = useUnreadNotificationCount()
  const markAllRead = useMarkAllNotificationsRead()
  const markRead = useMarkNotificationRead()
  const items = toNotificationItems(historyQuery.data ?? [])
  const unreadIds = items.filter((item) => !item.read).map((item) => item.id)

  /**
   * @description Marks every unread notification read.
   */
  const onMarkAllRead = () => markAllRead.mutate(unreadIds)

  /**
   * @description Marks one notification read.
   * @param {string} id - The notification's delivery id.
   */
  const onItemClick = (id: string) => markRead.mutate(id)

  return {
    items,
    unreadCount: unreadCountQuery.data ?? unreadIds.length,
    onMarkAllRead,
    onItemClick,
  }
}
